import { FC, useEffect, useState } from "react"
import {
  Button,
  Tabs,
  InputBase,
  Dialog,
  ArrowIcon16,
} from "@unlimint/admin-ui-kit"
import { useParams } from "react-router-dom"
import { Form, useFormikContext } from "formik"
import { useContextSelector as useCS } from "use-context-selector"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { isEmpty } from "lodash"

import { useTitle } from "domain/application/hooks/useTitle"
import {
  TCustomizationListItemRes,
  ThemeEnum,
} from "domain/customization/types"
import { FetchEntityContainer } from "domain/core/containers/FetchEntityContainer"
import {
  CustomizationContext,
  CustomizationProvider,
} from "domain/customization/CustomizationContext"
import { customizationSelectors } from "domain/customization/selectors"
import { useOpenable } from "domain/hooks/useOpenable"

import * as S from "./styled"
import { ThemeForm } from "./components/ThemeForm"
import { IntegrationInfo } from "../IntegrationInfo"
import { ChangesLogList } from "./components/ChangesLogList"
import { CustomizationThemePageContainer } from "./container"
import { ConfirmSaveThemes } from "./components/ConfirmSaveThemes"
import { ConfirmBack } from "./components/ConfirmBack"

const enum TabValues {
  LightTheme = "lightTheme",
  DarkTheme = "darkTheme",
  ChangesLog = "changesLog",
}

const tabs = [
  {
    value: TabValues.LightTheme,
    label: <S.TabsLabel>Light Theme</S.TabsLabel>,
  },
  {
    value: TabValues.DarkTheme,
    label: <S.TabsLabel>Dark Theme</S.TabsLabel>,
  },
  {
    value: TabValues.ChangesLog,
    label: <S.TabsLabel>Changes log</S.TabsLabel>,
  },
]

export type TCustomisactionThemePageProps = {
  data: TCustomizationListItemRes
  commonError: string | null
  saveOpenable: {
    open: boolean
    onOpen(): void
    onClose(): void
  }
  onResetCommonError(): void
  onBack(): void
  setIsBackFlow(v: boolean): void
}

const Page: FC<TCustomisactionThemePageProps> = ({
  data,
  commonError,
  onResetCommonError,
  saveOpenable,
  onBack,
  setIsBackFlow,
}) => {
  const {
    dirty,
    resetForm,
    isSubmitting,
    submitForm,
    errors,
    setErrors,
    isValid,
  } = useFormikContext()

  const backOpenable = useOpenable()

  const [currentTabValue, setCurrentTabValue] = useState(tabs[0].value)

  const handleSetCurrentTabValue = (v: TabValues) => {
    setCurrentTabValue(v)
  }

  const tabsContents = [
    {
      value: TabValues.LightTheme,
      content: (
        <S.TabContent>
          <ThemeForm theme={ThemeEnum.Light} />
        </S.TabContent>
      ),
    },
    {
      value: TabValues.DarkTheme,
      content: (
        <S.TabContent>
          <ThemeForm theme={ThemeEnum.Dark} />
        </S.TabContent>
      ),
    },
    {
      value: TabValues.ChangesLog,
      content: currentTabValue === TabValues.ChangesLog && (
        <S.TabContent>
          <ChangesLogList />
        </S.TabContent>
      ),
    },
  ]

  const saveError = !isEmpty(errors)
    ? "Invalid values. Check themes configs"
    : null
  const saveCommonError = commonError
    ? "Bad request. Something went wrong."
    : null

  useEffect(() => {
    if (saveOpenable.open) return
    setErrors({})
    onResetCommonError()
  }, [saveOpenable.open])

  useEffect(() => {
    if (backOpenable.open) return
    setIsBackFlow(false)
  }, [saveOpenable.open])

  const handleBack = (e: Event) => {
    setIsBackFlow(true)
    if (dirty) {
      backOpenable.onOpen(e)
      return
    }

    onBack()
  }

  return (
    <>
      <S.SettingsTopWrapp>
        <S.SettingsTop>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            style={{ textTransform: "none" }}
            onClick={handleBack}
          >
            <ArrowIcon16 sx={{ fontSize: 16 }} /> Go back
          </Button>

          <S.SettingsTopBtns>
            <Button
              size="small"
              disabled={!dirty}
              style={{ textTransform: "none" }}
              color="secondary"
              onClick={resetForm}
            >
              Discard changes
            </Button>
            <Button
              size="small"
              disabled={!dirty || !isValid}
              style={{ textTransform: "none" }}
              onClick={saveOpenable.onOpen}
            >
              Save changes
            </Button>
          </S.SettingsTopBtns>
        </S.SettingsTop>

        <S.SettingsTopShadow />
      </S.SettingsTopWrapp>

      <S.Wrapp>
        <S.Info>
          <IntegrationInfo />
        </S.Info>

        <S.NameWrapp>
          <S.NameLabel>Customization name (styleName)</S.NameLabel>
          <S.NameInp>
            <InputBase value={data.name} size="small" />
          </S.NameInp>
          <S.NameCopy>
            <CopyToClipboard text={data.name}>
              <Button
                size="small"
                style={{ textTransform: "none" }}
                color="secondary"
              >
                Copy to Clipboard
              </Button>
            </CopyToClipboard>
          </S.NameCopy>
        </S.NameWrapp>

        <Form>
          <Tabs
            tabs={tabs}
            tabsContents={tabsContents}
            initialTab={tabs[0].value}
            indicatorColor="secondary"
            themeOverride={S.TabsThemeOverride}
            onChange={handleSetCurrentTabValue}
          />
        </Form>
      </S.Wrapp>

      <Dialog open={saveOpenable.open} onClose={saveOpenable.onClose}>
        <ConfirmSaveThemes
          onCancel={saveOpenable.onClose}
          isSubmitting={isSubmitting}
          onSubmit={submitForm}
          error={saveError || saveCommonError}
        />
      </Dialog>

      <Dialog open={backOpenable.open} onClose={backOpenable.onClose}>
        <ConfirmBack
          onCancel={backOpenable.onClose}
          onBack={onBack}
          isSubmitting={isSubmitting}
          onSubmit={submitForm}
          error={saveError || saveCommonError}
        />
      </Dialog>
    </>
  )
}

export const CustomisactionThemePage = () => {
  const { name } = useParams()

  useTitle(name)

  return (
    <CustomizationProvider>
      <FetchEntityContainer
        url={`/v1/merchants/styles/${name}`}
        resourceContext={CustomizationContext}
        resourceSelectors={customizationSelectors}
      />
      <CustomizationThemePageContainer Component={Page} />
    </CustomizationProvider>
  )
}
